import { keyframes, styled } from '@mui/material';

const slideInFromRight = keyframes`
  0% { opacity: 0; transform: translateX(30px) scale(0.9); }
  100% { opacity: 1; transform: translateX(0) scale(1); }
`;

const slideInFromLeft = keyframes`
  0% { opacity: 0; transform: translateX(-30px) scale(0.9); }
  100% { opacity: 1; transform: translateX(0) scale(1); }
`;

const typingBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.25; }
  40% { transform: translateY(-3px); opacity: 0.9; }
`;

export const MessageWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser?: boolean }>(({ isUser }) => ({
  padding: '0.75rem 1rem',
  borderRadius: 20,
  maxWidth: '75%',
  wordWrap: 'break-word',
  lineHeight: 1.55,

  ...(isUser
    ? {
        alignSelf: 'flex-end',
        background: 'linear-gradient(135deg, #0a80e0 0%, #0868bd 100%)',
        color: '#fff',
        boxShadow: '0 2px 8px rgba(10, 128, 224, 0.25), 0 1px 3px rgba(10, 128, 224, 0.15)',
        animation: `${slideInFromRight} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        transformOrigin: 'right center',
      }
    : {
        alignSelf: 'flex-start',
        backgroundColor: '#f0f2f5',
        color: '#222',
        fontSize: '18px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        animation: `${slideInFromLeft} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        transformOrigin: 'left center',

        '& p': { margin: '0.35rem 0' },
        '& ul': { listStyle: 'disc', paddingLeft: '1.25rem', margin: '0.25rem 0 0.75rem' },
        '& ol': { listStyle: 'decimal', paddingLeft: '1.25rem', margin: '0.25rem 0 0.75rem' },
        '& li': { margin: '0.15rem 0' },
        '& h1, & h2, & h3': { fontSize: '1.1rem', margin: '0.5rem 0 0.25rem 0', fontWeight: 700 },
        '& a': { color: '#0b63ce', textDecoration: 'underline' },
        '& code': {
          background: '#f6f7f9',
          padding: '0.15rem 0.35rem',
          borderRadius: 4,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        },
        '& pre': {
          background: '#f6f7f9',
          padding: '0.75rem',
          borderRadius: 8,
          overflow: 'auto',
          margin: '0.5rem 0',
          '& code': { background: 'transparent' },
        },
      }),

  '@media (max-width: 480px)': {
    maxWidth: '85%',
  },
}));

export const CodeBlockWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  margin: '0.5rem 0',
  '& pre': {
    background: '#f6f7f9',
    padding: '0.75rem',
    borderRadius: 8,
    overflow: 'auto',
    margin: 0,
    width: '100%',
  },
});

export const CopyCode = styled('button')({
  position: 'static',
  background: 'rgba(10, 128, 224, 0.9)',
  border: 'none',
  borderRadius: 6,
  padding: '0.35rem 0.6rem',
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 28,
  zIndex: 10,
  marginTop: '0.4rem',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    background: 'rgba(10, 128, 224, 1)',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
});

export const TypingDots = styled('span')({
  display: 'inline-flex',
  gap: 4,
  marginLeft: 6,
  verticalAlign: 'middle',
  '& span': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'currentColor',
    opacity: 0.25,
    animation: `${typingBounce} 1.2s infinite ease-in-out`,
  },
  '& span:nth-of-type(2)': { animationDelay: '0.15s' },
  '& span:nth-of-type(3)': { animationDelay: '0.3s' },
});

export const Label = styled('strong')({
  fontWeight: 700,
});
